"use client";

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/components/ui/sonner";
import {
  claimToastGeneration,
  currentToastGeneration,
  releaseToastGeneration,
} from "@/lib/toast-dismiss-generation";
import { pluralize } from "@/lib/string/pluralize";
import type { SyncDirection } from "../sync-direction-icon";
import { useSyncJobPoll } from "../../hooks/use-sync-job-poll";
import { invalidateAfterSync } from "../../lib/invalidate-after-sync";

export const SYNC_CONFIRMING_ELAPSED_MS = 45_000;

function confirmingSuffix(ats: string): string {
  if (ats.toLowerCase().includes("ashby")) {
    return "Confirming with Ashby… creates can take a minute to appear.";
  }
  return `Confirming with ${ats}… remote creates can take a minute to appear.`;
}

export type SyncProgressSession = {
  sessionId: string;
  jobId: string;
  syncJobId: string;
  ats: string;
  direction?: SyncDirection | null;
  blockedCount: number;
  toastId: string;
  /** Accept-time wall clock — keeps soft-timeout across refresh resume. */
  startedAt: number;
};

export function SyncProgressToast({
  session,
  onFinished,
}: {
  session: SyncProgressSession;
  onFinished: (sessionId: string) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isTerminal, stuckStarting, timedOut, isError } = useSyncJobPoll({
    jobId: session.jobId,
    syncJobId: session.syncJobId,
    enabled: true,
    startedAtMs: session.startedAt,
  });

  const userDismissedRef = React.useRef(false);
  const closingRef = React.useRef(false); // terminal complete only
  const claimRef = React.useRef(0);

  // `total` is fixed at accept (submitted items). `push_eligible` grows during
  // enroll — use total for the title (bulk-move style) so it doesn't climb
  // mid-run; keep done/eligible for reconcile progress among enrolled.
  const total = data?.total ?? 0;
  const eligible = data?.push_eligible ?? 0;
  const done = (data?.push_ok ?? 0) + (data?.push_failed ?? 0);
  const progressVerb =
    session.direction === "push_only" ? "Exporting" : "Syncing";

  const title =
    total > 0
      ? `${progressVerb} ${total} ${pluralize(total, "profile")} to ${session.ats}`
      : `${progressVerb} profiles to ${session.ats}`;

  const description = React.useMemo(() => {
    if (stuckStarting) {
      return "Still starting… ATS sync hasn't begun yet.";
    }
    if (eligible === 0) {
      return "Starting…";
    }
    const parts = [`${done} / ${eligible}`];
    if (session.blockedCount > 0) {
      parts.push(`${session.blockedCount} blocked`);
    }
    const elapsed = Date.now() - session.startedAt;
    if (done < eligible && elapsed >= SYNC_CONFIRMING_ELAPSED_MS) {
      parts.push(confirmingSuffix(session.ats));
    }
    return parts.join(" · ");
  }, [
    stuckStarting,
    eligible,
    done,
    session.blockedCount,
    session.startedAt,
    session.ats,
  ]);
  React.useEffect(() => {
    if (userDismissedRef.current || closingRef.current) return;
    const g = claimToastGeneration(session.toastId);
    claimRef.current = g;
    showToast.loading(title, {
      id: session.toastId,
      description,
      duration: Infinity,
      onDismiss: () => {
        // Dismiss ≠ cancel — server work continues.
        if (closingRef.current) return;
        // Ignore stale onDismiss from StrictMode cleanup / sonner exit animation.
        if (currentToastGeneration(session.toastId) !== g) return;
        userDismissedRef.current = true;
        releaseToastGeneration(session.toastId, g);
        onFinished(session.sessionId);
      },
    });
  }, [title, description, session.toastId, session.sessionId, onFinished]);

  React.useEffect(() => {
    if (userDismissedRef.current || closingRef.current) return;

    if (isError) {
      // Poll failed even after retries — don't silently drop the toast. Mirror
      // the timeout branch: surface a warning so the user knows the sync may
      // still be running rather than leaving them with nothing.
      closingRef.current = true;
      releaseToastGeneration(session.toastId, claimRef.current);
      onFinished(session.sessionId);
      showToast.dismiss(session.toastId);
      showToast.warning("Sync is still running", {
        description:
          "We couldn't check its progress. Refresh the pipeline to see the latest.",
        duration: Infinity,
      });
      return;
    }

    if (timedOut) {
      closingRef.current = true;
      releaseToastGeneration(session.toastId, claimRef.current);
      onFinished(session.sessionId);
      showToast.dismiss(session.toastId);
      showToast.warning("Sync is still running", {
        description:
          "This is taking longer than expected. Refresh the pipeline to check progress.",
        duration: Infinity,
      });
      return;
    }

    if (!isTerminal || !data) return;

    closingRef.current = true;
    releaseToastGeneration(session.toastId, claimRef.current);
    invalidateAfterSync(queryClient, session.jobId);
    onFinished(session.sessionId);
    showToast.dismiss(session.toastId);

    if (data.status === "completed") {
      if (data.push_eligible === 0) {
        showToast.warning("None of these profiles could be queued", {
          description:
            "They may have been synced elsewhere. Close and try again.",
        });
      } else {
        const completedVerb =
          session.direction === "push_only" ? "Exported" : "Synced";
        showToast.success(
          `${completedVerb} ${data.push_ok} ${pluralize(data.push_ok, "profile")} to ${session.ats}`,
        );
      }
      return;
    }

    if (data.status === "completed_with_errors") {
      showToast.warning(
        `${data.push_failed} of ${data.push_eligible} failed`,
      );
      return;
    }

    if (data.status === "failed") {
      showToast.error("Sync failed to start", {
        description: data.error_message ?? undefined,
      });
    }
  }, [
    isTerminal,
    timedOut,
    isError,
    data,
    queryClient,
    session.ats,
    session.direction,
    session.jobId,
    session.toastId,
    session.sessionId,
    onFinished,
  ]);

  React.useEffect(() => {
    return () => {
      if (userDismissedRef.current || closingRef.current) return;
      // Bump so any in-flight onDismiss from this claim is stale. Defer dismiss
      // so StrictMode remount can reclaim the same toast id first; otherwise
      // sonner's delayed remove kills the remounted loading toast and can clear
      // the session via onDismiss.
      const expected = claimToastGeneration(session.toastId);
      queueMicrotask(() => {
        if (currentToastGeneration(session.toastId) !== expected) return;
        showToast.dismiss(session.toastId);
        releaseToastGeneration(session.toastId, expected);
      });
    };
  }, [session.toastId]);

  return null;
}
